import { localDate } from '../../components/dateRange';

export const formatLabel = value => String(value || '').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) || '—';
export const incidentId = value => `INC-${String(value).padStart(4, '0')}`;
export const isOpen = row => ['OPEN', 'IN_PROGRESS'].includes(row.status);
export const formatDate = value => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export function incidentAge(row, today = localDate()) {
  const occurred = new Date(row.incidentDateTime);
  if (Number.isNaN(occurred.getTime())) return null;
  return Math.max(0, Math.round((Date.parse(today) - Date.parse(localDate(occurred))) / 86400000));
}

export function complianceArea(row) {
  const slug = row.submission?.checklist?.slug;
  if (slug === 'temperature-check') return 'Temperature Control';
  if (slug === 'delivery-check') return 'Supplier / Traceability';
  if (['CUSTOMER_INJURY', 'STAFF_INJURY', 'SECURITY_INCIDENT', 'FIRE_SAFETY', 'CUSTOMER_COMPLAINT'].includes(row.type)) return 'Health & Safety';
  if (['ALLERGEN_DIETARY', 'FOOD_SPILL'].includes(row.type)) return 'Food Safety (Allergens)';
  if (['EQUIPMENT_FAILURE', 'PROPERTY_DAMAGE'].includes(row.type)) return 'Premises & Environment';
  return 'Other';
}

export function summarizeIncidentDashboard(records, today) {
  const open = records.filter(isOpen);
  const buckets = [
    { id: 1, label: '0 – 3 days', min: 0, max: 3, barBg: 'bg-emerald-500' },
    { id: 2, label: '4 – 7 days', min: 4, max: 7, barBg: 'bg-amber-400' },
    { id: 3, label: '8 – 14 days', min: 8, max: 14, barBg: 'bg-orange-400' },
    { id: 4, label: '15+ days', min: 15, max: Infinity, barBg: 'bg-rose-500' },
  ].map(bucket => {
    const count = open.filter(row => { const age = incidentAge(row, today); return age !== null && age >= bucket.min && age <= bucket.max; }).length;
    return { ...bucket, count, percentage: (open.length ? Math.round(count * 100 / open.length) : 0) + '%' };
  });
  return { open: open.length, overdue: open.filter(row => incidentAge(row, today) > 7).length,
    completed: records.filter(row => row.status === 'COMPLETED').length, aging: buckets,
  };
}

export function incidentEvidence(records) {
  const documents = new Map();
  for (const incident of records) for (const photo of incident.photos || []) {
    if (!photo.photoUrl || documents.has(photo.photoUrl)) continue;
    documents.set(photo.photoUrl, { id: photo.id, incidentId: incident.id, url: photo.photoUrl,
      name: `${incidentId(incident.id)} evidence`, meta: `${formatDate(photo.createdAt)} · ${formatLabel(incident.type)}` });
  }
  return [...documents.values()];
}

export function incidentActivity(records) {
  return records.flatMap(row => {
    const activity = [{ id: 'report-' + row.id, incidentId: row.id, title: 'Incident recorded', subtitle: `${incidentId(row.id)} • ${row.description}`,
      date: row.createdAt, type: 'report' }];
    if (row.updatedAt && row.createdAt && Date.parse(row.updatedAt) > Date.parse(row.createdAt)) activity.push({
      id: 'update-' + row.id, incidentId: row.id, title: 'Record updated', subtitle: `${incidentId(row.id)} • Current status: ${formatLabel(row.status)}`, date: row.updatedAt, type: 'update',
    });
    return activity;
  }).sort((a, b) => Date.parse(b.date || 0) - Date.parse(a.date || 0));
}

export function evidenceUrl(value) {
  if (!value) return '';
  try {
    const base = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || window.location.origin;
    const url = new URL(value, base + '/');
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}

export function incidentsCsv(records) {
  const rows = [['ID', 'Description', 'Type', 'Location', 'Incident date', 'Status', 'Reported by', 'Assigned to', 'Immediate action', 'Corrective action', 'Root cause'],
    ...records.map(row => [incidentId(row.id), row.description, formatLabel(row.type), row.location, row.incidentDateTime, formatLabel(row.status), row.createdBy?.fullName, row.referredTo?.fullName, row.immediateAction, row.correctiveAction, row.rootCause])];
  return '\uFEFF' + rows.map(row => row.map(value => {
    let text = String(value ?? '');
    if (/^[=+@\-\t\r]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  }).join(',')).join('\r\n');
}
